import { Button, UncontrolledTooltip } from 'reactstrap';
import styled from 'styled-components';
import { useLocation } from 'react-router';
import ChartIcon from '../icons/ChartIcon';
import SectionsIcon from '../icons/SectionsIcon';
import TableIcon from '../icons/TableIcon';
import { useLayoutContext } from '../../contexts/LayoutContext';
import { filterButtonStyles } from '../basics/FilterButton';
import {
  useDesignInfoModalContext,
  useDesignInfoModalTitle,
} from '../../contexts/DesignInfoModalContext';
import Shortcut from '../basics/Shortcut';
import { SHORTCUTS } from '../../constants/shortcuts';
import { captureEvent } from '../../utils/analytics';

const CHART_BUTTON_ID = 'toggle-chart-button';
const TABLE_BUTTON_ID = 'toggle-table-button';

const FilterButton = styled(Button)`
  ${filterButtonStyles}

  border-radius: 22.5px;
  border: none;

  display: flex;
  align-items: center;
  justify-content: center;
`;

const DesignInfoModalToggleButton = () => {
  const { isOpen, setIsOpen } = useDesignInfoModalContext();

  const modalTitle = useDesignInfoModalTitle();

  const { pathname } = useLocation();

  const ID = 'design-info-modal-toggle-button';

  return (
    <>
      <FilterButton
        id={ID}
        selected={isOpen}
        onClick={() =>
          setIsOpen((current) => {
            const newValue = !current;

            // track when users open the design info modal
            if (newValue) {
              // include 'page' in the event, which is just the segments of the path after /app/farm/<farm_id>/
              const page = pathname
                .split('/')
                .filter((s) => s)
                .slice(3)
                .join('/');
              captureEvent('design-panel-opened', { page });
            }

            return newValue;
          })
        }
      >
        <SectionsIcon className="m-0" />
      </FilterButton>
      <UncontrolledTooltip target={ID} innerClassName="font-tiny">
        {isOpen ? `Hide ${modalTitle}` : `Show ${modalTitle}`}
      </UncontrolledTooltip>
      <Shortcut
        shortcut={SHORTCUTS.TOGGLE_DESIGN_PANEL.key}
        handler={() => setIsOpen((x) => !x)}
      />
    </>
  );
};

const ChartButton = () => {
  const { summaryShown, setSummaryShown } = useLayoutContext();

  return (
    <>
      <FilterButton
        id={CHART_BUTTON_ID}
        className="ml-1"
        selected={summaryShown}
        onClick={() => setSummaryShown(!summaryShown)}
      >
        <ChartIcon className="m-0" />
      </FilterButton>
      <UncontrolledTooltip target={CHART_BUTTON_ID} innerClassName="font-tiny">
        {summaryShown ? `Hide Chart` : `Show Chart`}
      </UncontrolledTooltip>
      <Shortcut
        shortcut={SHORTCUTS.TOGGLE_CHART.key}
        handler={() => setSummaryShown((x) => !x)}
      />
    </>
  );
};

const TableButton = () => {
  const { tableShown, setTableShown } = useLayoutContext();

  return (
    <>
      <FilterButton
        id={TABLE_BUTTON_ID}
        className="ml-1"
        selected={tableShown}
        onClick={() => setTableShown(!tableShown)}
      >
        <TableIcon className="m-0" />
      </FilterButton>
      <UncontrolledTooltip target={TABLE_BUTTON_ID} innerClassName="font-tiny">
        {`${tableShown ? 'Hide' : 'Show'} Raw Dataset`}
      </UncontrolledTooltip>
    </>
  );
};

const HeaderToggleButtons = ({
  showChartButton = false,
  showTableButton = false,
}) => {
  return (
    <div className="ml-2 d-flex align-self-stretch">
      <DesignInfoModalToggleButton />
      {showChartButton && <ChartButton />}
      {showTableButton && <TableButton />}
    </div>
  );
};

export default HeaderToggleButtons;
